import { inject } from 'aurelia';
import { ICurrentRoute, IRouteContext, IRouter, Params } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import { UserApi } from '../../apis/user/user-api';
import { UserContext } from '../../contexts/user-context';
import { LanguageResolver } from '../../infrastructure/language-resolver';
import { PasskeyApi } from '../../apis/passkey/passkey-api';
import { LoginCodeApi } from '../../apis/login-code/login-code-api';
import { prepareAssertionOptions, serializeAssertionCredential } from '../../infrastructure/webauthn-helpers';
import { ProjectDraftStorageService } from '../../services/project-draft-storage-service';
import { ApiBase } from '../../apis/api-base';

type LoginStep = 'email' | 'methods' | 'code-entry';

@inject(IRouter, ICurrentRoute, IRouteContext, UserApi, UserContext, LanguageResolver, PasskeyApi, LoginCodeApi, I18N, ProjectDraftStorageService)
export class LoginPage {
    public returnUrl: string | null = null;
    public step: LoginStep = 'email';
    public email: string = '';
    public code: string = '';
    public hasPasskey: boolean = false;
    public isBusy: boolean = false;
    public errorMessage: string | null = null;
    public globalError: string | null = null;

    private readonly _router: IRouter;
    private readonly _currentRoute: ICurrentRoute;
    private readonly _routeContext: IRouteContext;
    private readonly _userApi: UserApi;
    private readonly _userContext: UserContext;
    private readonly _languageResolver: LanguageResolver;
    private readonly _passkeyApi: PasskeyApi;
    private readonly _loginCodeApi: LoginCodeApi;
    private readonly _i18n: I18N;
    private readonly _projectDraftStorageService: ProjectDraftStorageService;

    private _passkeyOptions: any = null;
    private _passkeyCacheKey: string = null;

    constructor(
        router: IRouter,
        currentRoute: ICurrentRoute,
        routeContext: IRouteContext,
        userApi: UserApi,
        userContext: UserContext,
        languageResolver: LanguageResolver,
        passkeyApi: PasskeyApi,
        loginCodeApi: LoginCodeApi,
        i18n: I18N,
        projectDraftStorageService: ProjectDraftStorageService,
    ) {
        this._router = router;
        this._currentRoute = currentRoute;
        this._routeContext = routeContext;
        this._userApi = userApi;
        this._userContext = userContext;
        this._languageResolver = languageResolver;
        this._passkeyApi = passkeyApi;
        this._loginCodeApi = loginCodeApi;
        this._i18n = i18n;
        this._projectDraftStorageService = projectDraftStorageService;
    }


    public loading(): void {

        this.returnUrl = this._currentRoute.query.get('returnUrl');
        // error arrives via full-page OAuth redirect; URL is committed by then
        const urlError = this._currentRoute.query.get('error') ?? new URLSearchParams(window.location.search).get('error');
        if (urlError === 'provider_mismatch') {
            this.globalError = this._i18n.tr('ui:login_provider_mismatch');
        }
    }
    public signInWithProvider(provider: string): void {
        this._projectDraftStorageService.saveDraft();
        const langPath = `/${this._languageResolver.currentLanguage}/app/`;
        const target = this.returnUrl
            ? `${window.location.origin}${langPath}#${this.returnUrl}`
            : `${window.location.origin}${langPath}`;
        window.location.href = `/auth/signin/${provider}?returnUrl=${encodeURIComponent(target)}`;
    }

    public async lookupEmail(): Promise<void> {
        if (!this.email) return;
        this.isBusy = true;
        this.errorMessage = null;
        try {
            const { options, cacheKey } = await this._passkeyApi.loginBegin(this.email);
            this.hasPasskey = !!(options?.allowCredentials?.length);
            this._passkeyOptions = options;
            this._passkeyCacheKey = cacheKey;
        } catch {
            this.hasPasskey = false;
        } finally {
            this.step = 'methods';
            this.isBusy = false;
        }
    }

    public async signInWithPasskey(): Promise<void> {
        this.isBusy = true;
        this.errorMessage = null;
        try {
            const requestOptions = prepareAssertionOptions(this._passkeyOptions);
            const credential = await navigator.credentials.get({ publicKey: requestOptions }) as PublicKeyCredential | null;
            if (!credential) { return; }
            const assertion = serializeAssertionCredential(credential);
            await this._passkeyApi.loginComplete({ cacheKey: this._passkeyCacheKey, assertionResponse: JSON.stringify(assertion) });
            await this.onAuthSuccess();
        } catch (e: any) {
            if ((e as DOMException)?.name === 'NotAllowedError') { return; }
            this.errorMessage = e?.content?.title ?? (typeof e?.content === 'string' ? e.content : null) ?? e?.message ?? (e?.statusCode ? `Error ${e.statusCode as number}` : String(e));
        } finally {
            this.isBusy = false;
        }
    }

    public async sendEmailCode(): Promise<void> {
        if (!this.email) return;
        this.isBusy = true;
        this.errorMessage = null;
        try {
            await this._loginCodeApi.requestCode(this.email);
            this.step = 'code-entry';
        } catch (e: any) {
            this.errorMessage = e?.statusCode === 429
                ? this._i18n.tr('ui:login_too_many_requests')
                : e?.content?.title ?? (typeof e?.content === 'string' ? e.content : null) ?? e?.message ?? String(e);
        } finally {
            this.isBusy = false;
        }
    }

    public async verifyEmailCode(): Promise<void> {
        if (!this.email || !this.code) return;
        this.isBusy = true;
        this.errorMessage = null;
        try {
            await this._loginCodeApi.verifyCode(this.email, this.code);
            await this.onAuthSuccess();
        } catch (e: any) {
            this.errorMessage = e?.content?.message === 'invalid_or_expired_token'
                ? this._i18n.tr('ui:email_code_invalid')
                : e?.content?.title ?? (typeof e?.content === 'string' ? e.content : null) ?? e?.message ?? String(e);
        } finally {
            this.isBusy = false;
        }
    }

    public goBack(): boolean {
        this.step = this.step === 'code-entry' ? 'methods' : 'email';
        this.errorMessage = null;
        return false; // prevents href="#" scroll-to-top
    }

    public async onAuthSuccess(): Promise<void> {
        // Refresh CSRF: token was for the anonymous session, now the identity changed.
        await ApiBase.refreshCsrfToken();
        try {
            const user = await this._userApi.getCurrentUser();
            if (user) {
                this._userContext.setUserInfo(user);
            }
        } catch { /* ignore */ }
        if (this._userContext.userEntity.termsAcceptedAt == null) {
            const onboardingRoute = this.returnUrl ? `onboarding?returnUrl=${encodeURIComponent(this.returnUrl)}` : 'onboarding';
            void this._router.load(onboardingRoute);
        } else if (this.returnUrl) {
            void this._router.load(this.returnUrl);
        } else {
            void this._router.load('my-events');
        }
    }
}

